import { CapMeter } from './CapMeter'
import { HScroll } from './HScroll'
import { CAT_LABEL, NAV_CAP_PCT, type Rung, type SleeveContent } from './strategyContent'
import type { SleeveLive } from './useStrategyLive'

interface Props {
  sleeve: SleeveContent
  live: SleeveLive
  liveAvailable: boolean
  narrow: boolean
  open: boolean
  onToggle: () => void
}

const MAX_METERS = 5
const fmt = (v: number | null, d = 1) => (v == null ? 'n/a' : v.toFixed(d))

function chipFor(r: Rung): { text: string; cls: string } {
  if (r.kind === 'removed') return { text: r.chip ?? 'removed', cls: 'strat-chip--removed' }
  if (r.kind === 'review') return { text: 'under review', cls: 'strat-chip--review' }
  if (r.kind === 'dynamic') return { text: 'dynamic', cls: 'strat-chip--dynamic' }
  return { text: 'static', cls: '' }
}

function RungCard({ rung, n }: { rung: Rung; n: number }) {
  const chip = chipFor(rung)
  const gone = rung.kind === 'removed'
  return (
    <li className={`strat-rung strat-rung--${rung.cat}${gone ? ' strat-rung--removed' : ''}`}>
      <span className="strat-rung-n">{n}</span>
      <span className="strat-rung-cat">{CAT_LABEL[rung.cat]}</span>
      <h4 className="strat-rung-title">{gone ? <s>{rung.title}</s> : rung.title}</h4>
      <span className={`strat-chip ${chip.cls}`}>{chip.text}</span>
      <p className="strat-rung-plain">{rung.plain}</p>
      {rung.anchor && <p className="strat-rung-anchor">{rung.anchor}</p>}
      <code className="strat-rung-band">{gone ? <s>{rung.band}</s> : rung.band}</code>
    </li>
  )
}

export function SleeveLadder({ sleeve, live, liveAvailable, narrow, open, onToggle }: Props) {
  const id = `strat-panel-${sleeve.key}`
  const target = `${fmt(live.targetPct)}% target`
  const showPanel = !narrow || open
  const head = (
    <>
      <span className="strat-sleeve-name">{sleeve.name}</span>
      <span className="strat-sleeve-target">{target}</span>
      {narrow && <span className="strat-sleeve-mini">scalar {fmt(live.scalar, 2)}</span>}
      {!narrow && <span className="strat-sleeve-meta">Benchmark: {sleeve.bench}. {sleeve.meta}</span>}
      {narrow && <span className="strat-chevron" aria-hidden="true">{open ? '▴' : '▾'}</span>}
    </>
  )
  const shown = live.positions.slice(0, MAX_METERS)
  return (
    <article className={`strat-sleeve strat-sleeve--${sleeve.key}`}>
      <h3 className="strat-sleeve-head">
        {narrow ? (
          <button type="button" className="strat-sleeve-btn" aria-expanded={open} aria-controls={id} onClick={onToggle}>{head}</button>
        ) : <div className="strat-sleeve-btn strat-sleeve-btn--static">{head}</div>}
      </h3>
      <div id={id} hidden={!showPanel}>
        {narrow && <p className="strat-sleeve-meta strat-sleeve-meta--m">Benchmark: {sleeve.bench}. {sleeve.meta}</p>}
        <HScroll className="strat-ladder" label={`${sleeve.name} entry ladder, ${sleeve.rungs.length} rungs`}>
          <ol className="strat-rungs">
            {sleeve.rungs.map((r, i) => <RungCard key={i} rung={r} n={i + 1} />)}
            <li className="strat-go">
              <span className="strat-rung-n">GO</span>
              <h4 className="strat-rung-title">It buys at the next open</h4>
              <ul>
                <li>Every active rung passed</li>
                <li>Ranked by gates passed, then RS margin</li>
                <li>Sleeve under target x regime</li>
                <li>Order fits the D080 per-position cap</li>
                <li>Market-on-open buy plus resting stop</li>
              </ul>
            </li>
          </ol>
        </HScroll>
        <div className="strat-foot">
          <div className="strat-box strat-box--live">
            <h4>Live</h4>
            <dl className="strat-kv">
              <dt>Sleeve target</dt><dd>{fmt(live.targetPct)}% of NAV</dd>
              <dt>Regime scalar</dt><dd>{fmt(live.scalar, 2)}x</dd>
              <dt>Scaled target</dt><dd>{live.scaledTargetPct == null ? 'n/a' : `${live.scaledTargetPct.toFixed(1)}% of NAV`}</dd>
              <dt>Effective position cap</dt><dd>{live.capPct == null ? 'n/a' : `${live.capPct.toFixed(2)}% of NAV`}</dd>
              {liveAvailable && <><dt>Deployed</dt><dd>{live.deployedPct.toFixed(1)}% of NAV, {live.positions.length} positions</dd></>}
            </dl>
            {liveAvailable ? (
              live.positions.length === 0 ? <p className="strat-note">No open positions in this sleeve.</p> : (
                <div className="strat-meters">
                  {shown.map(p => <CapMeter key={p.ticker} ticker={p.ticker} pctNav={p.pctNav} capPct={live.capPct} />)}
                  {live.positions.length > MAX_METERS && <p className="strat-note">+{live.positions.length - MAX_METERS} smaller positions not shown</p>}
                </div>
              )
            ) : <p className="strat-note strat-note--warn">Live data unavailable: position sizes not shown.</p>}
          </div>
          <div className="strat-box">
            <h4>Position size (D080)</h4>
            <p>Cap = min({NAV_CAP_PCT[sleeve.key]}% of NAV, 20% of the sleeve's regime-scaled target). It replaces the old fixed share per name.</p>
            <h4>Initial stop</h4>
            <p>{sleeve.stop}</p>
          </div>
          <div className="strat-box">
            <h4>Regime dial (sleeve size x this)</h4>
            <p>{sleeve.regime.title}</p>
            <p className="strat-note">{sleeve.regime.note}</p>
          </div>
        </div>
      </div>
    </article>
  )
}
